import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useLocation, useNavigate } from "react-router-dom";
import SeoMeta from "../../../../platform/components/common/seoMeta";

const SHARE_PATH = "/referrals";

const HOW_IT_WORKS = [
  "Enter a city or zip code.",
  "Complete the referral form.",
  "We review the request and route it to the right next step.",
];

export default function ReferrerLandingPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const initialLocation = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get("prefillLocation") || "").trim();
  }, [location.search]);

  const [locationInput, setLocationInput] = React.useState(initialLocation);
  const [locationError, setLocationError] = React.useState("");

  React.useEffect(() => {
    setLocationInput(initialLocation);
  }, [initialLocation]);

  const handleContinue = React.useCallback(() => {
    if (!locationInput.trim()) {
      setLocationError("Enter a city or zip code to continue.");
      return;
    }

    const params = new URLSearchParams(location.search);
    params.delete("q");
    params.set("prefillLocation", locationInput.trim());

    navigate({
      pathname: "/referrals/start",
      search: params.toString() ? `?${params.toString()}` : "",
    });
  }, [location.search, locationInput, navigate]);

  return (
    <>
      <SeoMeta
        title="Find Care Where You Need It | Plany365 Referrals"
        description="Start with the city or zip code where care is needed, then continue to the resident referral form."
        url={SHARE_PATH}
      />

      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #edf7ff 0%, #ffffff 38%, #f7f1e4 100%)",
          py: { xs: 4, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 5,
              border: "1px solid rgba(53,90,125,0.14)",
              backgroundColor: "rgba(255,255,255,0.94)",
              boxShadow: "0 28px 70px rgba(18,44,67,0.10)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background:
                  "radial-gradient(circle at top left, rgba(13,91,102,0.12), transparent 32%), radial-gradient(circle at bottom right, rgba(34,55,74,0.10), transparent 34%)",
              }}
            />

            <Grid container>
              <Grid item xs={12} md={7}>
                <Box sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
                  <Stack spacing={2.1}>
                    <Typography
                      variant="overline"
                      sx={{
                        letterSpacing: "0.14em",
                        fontWeight: 800,
                        color: "#4f6b83",
                      }}
                    >
                      Resident Referral
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: { xs: "2rem", sm: "2.5rem", md: "3.2rem" },
                        lineHeight: 0.98,
                        fontWeight: 900,
                        color: "#14324a",
                        maxWidth: 620,
                      }}
                    >
                      Find care where you need it.
                    </Typography>

                    <Typography
                      sx={{
                        maxWidth: 610,
                        color: "#49657a",
                        fontSize: { xs: "1rem", md: "1.08rem" },
                        lineHeight: 1.72,
                      }}
                    >
                      Start with the city or zip code where care is needed, then
                      continue to the referral form.
                    </Typography>

                    <Stack spacing={0.7} sx={{ pt: 0.5 }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.25}
                        alignItems={{ xs: "stretch", sm: "flex-start" }}
                      >
                        <TextField
                          fullWidth
                          value={locationInput}
                          label="Enter city or zip code"
                          onChange={(event) => {
                            setLocationInput(event.target.value);
                            if (locationError) setLocationError("");
                          }}
                          error={Boolean(locationError)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              handleContinue();
                            }
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "#fff",
                              borderRadius: 999,
                            },
                          }}
                        />
                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleContinue}
                          startIcon={<SearchRoundedIcon />}
                          sx={{
                            alignSelf: { xs: "stretch", sm: "flex-start" },
                            borderRadius: 999,
                            px: 2.8,
                            py: 1.25,
                            minHeight: 56,
                            minWidth: { xs: "100%", sm: 190 },
                            fontWeight: 800,
                            textTransform: "none",
                            bgcolor: "#0d5b66",
                            boxShadow: "0 14px 32px rgba(13,91,102,0.22)",
                            "&:hover": { bgcolor: "#0a4b54" },
                          }}
                        >
                          Continue
                        </Button>
                      </Stack>

                      <Typography
                        sx={{
                          pl: { xs: 0.25, sm: 1.75 },
                          color: locationError ? "#d32f2f" : "#6a8091",
                          fontSize: 13,
                          fontWeight: locationError ? 600 : 500,
                        }}
                      >
                        {locationError || "For example: Seattle, WA or 98116"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip
                        label="Individuals & families"
                        sx={{
                          borderRadius: 999,
                          bgcolor: alpha("#0d5b66", 0.08),
                          color: "#0d5b66",
                          fontWeight: 700,
                        }}
                      />
                      <Chip
                        label="Care teams (social workers, discharge planners)"
                        sx={{
                          borderRadius: 999,
                          bgcolor: alpha("#0d5b66", 0.08),
                          color: "#0d5b66",
                          fontWeight: 700,
                        }}
                      />
                      <Chip
                        label="Community partners (case managers, advocates)"
                        sx={{
                          borderRadius: 999,
                          bgcolor: alpha("#0d5b66", 0.08),
                          color: "#0d5b66",
                          fontWeight: 700,
                        }}
                      />
                    </Stack>
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box sx={{ p: { xs: 2.5, sm: 4, md: 5 }, height: "100%" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: { xs: 2.25, md: 2.75 },
                      borderRadius: 4,
                      border: "1px solid rgba(13,91,102,0.14)",
                      background:
                        "linear-gradient(180deg, rgba(13,91,102,0.06) 0%, rgba(255,255,255,0.95) 100%)",
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 800, color: "#0d5b66" }}
                      >
                        How it works
                      </Typography>
                      <Typography
                        sx={{
                          color: "#14324a",
                          fontSize: { xs: 20, md: 24 },
                          fontWeight: 800,
                          lineHeight: 1.15,
                        }}
                      >
                        Start with location and continue when you're ready.
                      </Typography>

                      <Stack spacing={1.2}>
                        {HOW_IT_WORKS.map((step, index) => (
                          <Box
                            key={step}
                            sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                bgcolor: "#22374a",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 13,
                                fontWeight: 800,
                                flexShrink: 0,
                              }}
                            >
                              {index + 1}
                            </Box>
                            <Typography sx={{ color: "#4f697d", lineHeight: 1.65 }}>
                              {step}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: "#fff",
                          border: "1px solid rgba(53,90,125,0.12)",
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, color: "#14324a", mb: 0.75 }}>
                          Referrer note
                        </Typography>
                        <Typography sx={{ color: "#4f697d", lineHeight: 1.65 }}>
                          Referrers never pay to send a referral.
                        </Typography>
                      </Paper>
                    </Stack>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
